import { inject, Injectable } from '@angular/core';
import { interval } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { BrowserEnvironmentService } from '../../browser-environment.service';
import { HostApplicationService } from '../host-application.service';
import { HostAppContext } from '../host-application.interfaces';

@Injectable({
  providedIn: 'root'
})
export class GainsightAnalyticsService {
  #hostApplicationService = inject(HostApplicationService);
  #environmentService = inject(BrowserEnvironmentService);

  public async init(): Promise<void> {
    try {
      if (this.#environmentService.gainsightPxKey) {
        await this.#setScript();

        if (typeof aptrinsic !== 'undefined') {
          const context = this.#hostApplicationService.getCurrentContext();
          if (context) {
            await this.#configure(context);
          } else {
            console.log("Gainsight configuration skipped - no context available");
          }
        }
      } else {
        console.warn('No Gainsight tag key');
      }
    } catch (e) {
      console.error(e);
    }
  }

  async #setScript() {
    const sdkName = 'aptrinsic';
    // @ts-ignore
    window[sdkName] = window[sdkName] || function (...args: any[]) {
      // @ts-ignore
      (window[sdkName].q = window[sdkName].q || []).push(...args);
    };
    // @ts-ignore
    window[sdkName].p = this.#environmentService.gainsightPxKey;
    // @ts-ignore
    window[sdkName].c = { iframeModeEnabled: false };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://web-sdk-eu.aptrinsic.com/api/aptrinsic.js?a=' + this.#environmentService.gainsightPxKey;
    document.head.appendChild(script);

    await new Promise<void>((resolve, reject) => {
      script.onload = () => {
        console.log('Gainsight SDK loading...');
        if (aptrinsic.init) {
          console.log('Gainsight SDK initialized: ', aptrinsic.init);

          resolve();
          return;
        }

        // Fallback in case script load was delayed
        const sub = interval(250).pipe(take(5), finalize(() => {
          if (!aptrinsic.init) {
            reject(new Error("Gainsight failed to initialize after 5 retries."));
          }
        })).subscribe(() => {
          console.log('Gainsight SDK init delayed retry: ', aptrinsic.init);
          if (aptrinsic.init) {
            sub.unsubscribe();
            resolve();
          }
        });
      };
      script.onerror = () => {
        // The script may have been blocked by an ad blocker
        reject(new Error("Gainsight failed to load the script. Please disable ad blockers or add site to the excluded list."));
      };
    });
  }

  async #configure(context: HostAppContext): Promise<void> {
    console.debug('Configuring Gainsight...');

    let accountId = "N/A";
    let accountName = "N/A";
    if (Object.hasOwn(context, 'fc.fleetcomplete.com')) {
      accountId = context['fc.fleetcomplete.com']?.org_id || 'N/A';
      accountName = context['fc.fleetcomplete.com']?.org_name || 'N/A';
    }
    else if (Object.hasOwn(context, 'on_road.unityaiot.com')) {
      accountId = context['on_road.unityaiot.com']?.org_id || 'N/A';
      accountName = context['on_road.unityaiot.com']?.org_name || 'N/A';
    }

    aptrinsic('identify',
      {
        id: context['embedded.aura.unityaiot.com'].user_id,
        user_id: context['embedded.aura.unityaiot.com'].user_email,
        firstName: context['embedded.aura.unityaiot.com'].user_first_name,
        lastName: context['embedded.aura.unityaiot.com'].user_last_name,
      },
      {
        id: accountId,
        name: accountName,
      }
    );
    console.debug("Gainsight is initialized: ", aptrinsic.init);
  }

  public trackEvent(eventName: string, properties: Record<string, string | number | boolean | null> = {}): void {
    try {
      if (typeof aptrinsic !== 'undefined') {
        aptrinsic('track', eventName, properties);
      }
    } catch (e) {
      console.error('Gainsight trackEvent error:', e);
    }
  }
}
